import { z } from 'zod'
import { salesSettingsShape } from '../dto/sales-setup.dto.js'
import { templateErrors } from '../helpers/contract-template.js'
import { scheduleEntryInputSchema, scheduleErrors } from '../helpers/sales-schedule.js'

/** A venue's setup for Sales, each list saved whole (docs/sales.spec.md). A row left out is removed. */

const name = z.string().trim().min(1, 'Give it a name.').max(120, 'That name is too long.')

export const putSalesTemplatesInputSchema = z
  .object({
    templates: z
      .array(
        z
          .object({
            id: z.string().uuid().optional(),
            name,
            body: z.string().max(50_000, 'That contract is too long.'),
          })
          .strict(),
      )
      .max(20, 'That’s too many templates.'),
  })
  .strict()
  .superRefine((input, ctx) => {
    const ids = new Set<string>()
    input.templates.forEach((t, i) => {
      if (t.id !== undefined) {
        if (ids.has(t.id)) ctx.addIssue({ code: 'custom', message: 'A contract is in the list twice.', path: ['templates', i, 'id'] })
        ids.add(t.id)
      }
      for (const message of templateErrors(t.body)) ctx.addIssue({ code: 'custom', message: `${t.name}: ${message}`, path: ['templates', i, 'body'] })
    })
  })
export type PutSalesTemplatesInput = z.infer<typeof putSalesTemplatesInputSchema>

export const putSalesSchedulesInputSchema = z
  .object({
    schedules: z
      .array(z.object({ id: z.string().uuid().optional(), name, entries: z.array(scheduleEntryInputSchema) }).strict())
      .max(20, 'That’s too many schedules.'),
  })
  .strict()
  .superRefine((input, ctx) => {
    const ids = new Set<string>()
    input.schedules.forEach((s, i) => {
      if (s.id !== undefined) {
        if (ids.has(s.id)) ctx.addIssue({ code: 'custom', message: 'A schedule is in the list twice.', path: ['schedules', i, 'id'] })
        ids.add(s.id)
      }
      for (const message of scheduleErrors(s.entries, true)) ctx.addIssue({ code: 'custom', message: `${s.name}: ${message}`, path: ['schedules', i, 'entries'] })
    })
  })
export type PutSalesSchedulesInput = z.infer<typeof putSalesSchedulesInputSchema>

export const putSalesSettingsInputSchema = z
  .object(salesSettingsShape)
  .strict()
  .refine((s) => new Set(s.reminderAfterDays).size === s.reminderAfterDays.length, {
    message: 'Each late reminder needs a different day.',
    path: ['reminderAfterDays'],
  })
export type PutSalesSettingsInput = z.infer<typeof putSalesSettingsInputSchema>
